import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

import { useI18n } from "../../../i18n/I18nContext";
import useCompaniesMap from "../../hooks/useCompaniesMap";
import HotelsMap from "./Map";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const multiKeys = [
    "classification_ids",
    "hotel_group_ids",
    "cuisine_ids",
    "restaurant_price_ids",
    "award_ids",
    "room_detail_ids",
    "amenity_ids",
    "leisure_activity_ids",
    "location_ids",
    "region_ids",
];

const goldButtonSx = {
    background: "linear-gradient(135deg, var(--domain-primary-light) 0%, var(--domain-primary) 100%)",
    color: "var(--domain-primary-contrast)",
    fontWeight: 700,
    fontSize: "0.875rem",
    textTransform: "none",
    px: 2,
    py: 1.25,
    borderRadius: 2.5,
    letterSpacing: "0.01em",
    boxShadow: "0 2px 12px rgba(240,172,77,0.30)",
    whiteSpace: "nowrap",
    "&:hover": {
        background: "linear-gradient(135deg, var(--domain-text) 0%, var(--domain-text) 100%)",
        boxShadow: "0 4px 20px rgba(26,37,56,0.25)",
        "& .MuiButton-endIcon": { transform: "translateX(4px)" },
    },
};

const outlineButtonSx = {
    ...goldButtonSx,
    background: "transparent",
    color: "var(--domain-primary)",
    boxShadow: "none",
    border: "1.5px solid var(--domain-primary)",
    "&:hover": {
        background: "linear-gradient(135deg, var(--domain-text) 0%, var(--domain-text) 100%)",
        borderColor: "transparent",
        color: "#fff",
        boxShadow: "0 4px 20px rgba(26,37,56,0.25)",
        "& .MuiButton-endIcon": { transform: "rotate(-20deg)" },
    },
};

function createEmptyFilters(defaultType) {
    return {
        q: "",
        country: "all",
        type: defaultType || "all",
        price_min: "",
        price_max: "",
        classification_ids: [],
        hotel_group_ids: [],
        cuisine_ids: [],
        restaurant_price_ids: [],
        award_ids: [],
        room_detail_ids: [],
        amenity_ids: [],
        leisure_activity_ids: [],
        location_ids: [],
        region_ids: [],
    };
}

function parseIds(value) {
    return (value || "")
        .split(",")
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item) && item > 0);
}

function filtersFromParams(searchParams, defaultType) {
    const filters = {
        ...createEmptyFilters(defaultType),
        q: searchParams.get("q") || "",
        country: searchParams.get("country") || "all",
        type: searchParams.get("type") || defaultType || "all",
        price_min: searchParams.get("price_min") || "",
        price_max: searchParams.get("price_max") || "",
    };
    multiKeys.forEach((key) => {
        filters[key] = parseIds(searchParams.get(key));
    });
    return filters;
}

function getLabel(option, lang) {
    if (!option) return "";
    return option[`label_${lang}`] || option.label_de || option.label_en || option.label_fr || option.label_it || option.label || option.name || "";
}

function selectedOptions(options, ids) {
    const idSet = new Set((ids || []).map(Number));
    return (options || []).filter((option) => idSet.has(Number(option.id)));
}

function toIdList(options) {
    return (options || []).map((option) => Number(option.id)).filter(Boolean);
}

function MultiFilter({ label, options, value, onChange, lang }) {
    return (
        <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            options={options || []}
            value={selectedOptions(options, value)}
            onChange={(_event, nextValue) => onChange(toIdList(nextValue))}
            getOptionLabel={(option) => getLabel(option, lang)}
            isOptionEqualToValue={(option, selected) => Number(option.id) === Number(selected.id)}
            renderInput={(params) => <TextField {...params} label={label} />}
            ChipProps={{ size: "small" }}
        />
    );
}

export default function SearchMapSection({
    defaultType = "all",
    filterProfile = "all",
    lockType = false,
    onCompaniesChange,
}) {
    const { t, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilters = useMemo(() => {
        const filters = filtersFromParams(searchParams, defaultType);
        if (lockType) filters.type = defaultType || "all";
        if (filterProfile === "restaurant") {
            filters.hotel_group_ids = [];
            filters.room_detail_ids = [];
            filters.price_min = "";
            filters.price_max = "";
        }
        return filters;
    }, [searchParams, defaultType, filterProfile, lockType]);
    const [draftFilters, setDraftFilters] = useState(activeFilters);
    const [filterOptions, setFilterOptions] = useState({
        categories: [],
        classifications: [],
        hotel_groups: [],
        cuisines: [],
        restaurant_prices: [],
        awards: [],
        room_details: [],
        amenities: [],
        leisure_activities: [],
        locations: [],
        regions: [],
        countries: [],
    });
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [optionsError, setOptionsError] = useState("");
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

    useEffect(() => {
        setDraftFilters(activeFilters);
    }, [activeFilters]);

    useEffect(() => {
        let cancelled = false;
        async function fetchFilterOptions() {
            setOptionsLoading(true);
            setOptionsError("");
            try {
                const res = await axios.get(`${API_BASE}/api/filteredcompanies/filter-options`, { withCredentials: true });
                if (!cancelled) setFilterOptions((current) => ({ ...current, ...(res.data || {}) }));
            } catch (err) {
                if (!cancelled) setOptionsError(err?.response?.data?.error || err?.message || "Filter konnten nicht geladen werden.");
            } finally {
                if (!cancelled) setOptionsLoading(false);
            }
        }
        fetchFilterOptions();
        return () => { cancelled = true; };
    }, []);

    const categoryIdsByType = useMemo(() => {
        const result = { hotel: [], restaurant: [] };
        (filterOptions.categories || []).forEach((category) => {
            const text = [category.name, category.label_de, category.label_en, category.label_fr, category.label_it].filter(Boolean).join(" ").toLowerCase();
            if (text.includes("hotel")) result.hotel.push(Number(category.id));
            if (text.includes("restaurant")) result.restaurant.push(Number(category.id));
        });
        return result;
    }, [filterOptions.categories]);

    const apiFilters = useMemo(() => {
        const filters = {};
        if (activeFilters.q.trim()) filters.q = activeFilters.q.trim();
        if (activeFilters.country !== "all") filters.country = activeFilters.country;
        if (activeFilters.type === "hotel") filters.category_ids = categoryIdsByType.hotel.length ? categoryIdsByType.hotel : [1];
        if (activeFilters.type === "restaurant") filters.category_ids = categoryIdsByType.restaurant.length ? categoryIdsByType.restaurant : [2];
        if (filterProfile !== "restaurant" && activeFilters.price_min.trim()) filters.price_min = activeFilters.price_min.trim();
        if (filterProfile !== "restaurant" && activeFilters.price_max.trim()) filters.price_max = activeFilters.price_max.trim();
        multiKeys.forEach((key) => {
            if (filterProfile === "restaurant" && ["hotel_group_ids", "room_detail_ids"].includes(key)) return;
            if (activeFilters[key]?.length) filters[key] = activeFilters[key];
        });
        return filters;
    }, [activeFilters, categoryIdsByType, filterProfile]);

    const { companies, markers, loading, error } = useCompaniesMap(apiFilters);
    const filteredMarkers = markers || [];

    useEffect(() => {
        if (typeof onCompaniesChange === "function") onCompaniesChange(companies || []);
    }, [companies, onCompaniesChange]);

    const updateDraft = (key, value) => {
        setDraftFilters((current) => ({ ...current, [key]: value }));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const params = new URLSearchParams();
        if (draftFilters.q.trim()) params.set("q", draftFilters.q.trim());
        if (draftFilters.country !== "all") params.set("country", draftFilters.country);
        if (!lockType && draftFilters.type !== (defaultType || "all")) params.set("type", draftFilters.type);
        if (draftFilters.price_min.trim()) params.set("price_min", draftFilters.price_min.trim());
        if (draftFilters.price_max.trim()) params.set("price_max", draftFilters.price_max.trim());
        multiKeys.forEach((key) => {
            if (draftFilters[key]?.length) params.set(key, draftFilters[key].join(","));
        });
        setSearchParams(params);
    };

    return (
        <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && !filteredMarkers.length ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: "var(--domain-primary)" }} />
                </Box>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 3, alignItems: "stretch" }}>
                    <Paper elevation={3} sx={{ borderRadius: 2, p: 2.5 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {filterProfile === "restaurant" ? t("map.search.restaurantTitle") : t("map.search.title")}
                        </Typography>

                        <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <TextField label={t("map.search.queryPlaceholder")} value={draftFilters.q} onChange={(e) => updateDraft("q", e.target.value)} size="small" fullWidth />

                            {!lockType && (
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t("map.search.typeLabel")}</InputLabel>
                                    <Select label={t("map.search.typeLabel")} value={draftFilters.type} onChange={(e) => updateDraft("type", e.target.value)} MenuProps={{ disableScrollLock: true }}>
                                        <MenuItem value="all">{t("map.search.typeAll")}</MenuItem>
                                        <MenuItem value="hotel">{t("map.search.typeHotels")}</MenuItem>
                                        <MenuItem value="restaurant">{t("map.search.typeRestaurants")}</MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            <FormControl fullWidth size="small">
                                <InputLabel>{t("map.search.countryLabel")}</InputLabel>
                                <Select label={t("map.search.countryLabel")} value={draftFilters.country} onChange={(e) => updateDraft("country", e.target.value)} MenuProps={{ disableScrollLock: true }}>
                                    <MenuItem value="all">{t("map.search.countryAll")}</MenuItem>
                                    {(filterOptions.countries || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <Divider />

                            <MultiFilter label={t("map.search.classifications")} options={filterOptions.classifications} value={draftFilters.classification_ids} onChange={(value) => updateDraft("classification_ids", value)} lang={lang} />

                            <Accordion disableGutters elevation={0} expanded={advancedFiltersOpen} onChange={(_event, expanded) => setAdvancedFiltersOpen(expanded)} sx={{ border: "1px solid #e0e5eb", borderRadius: "8px !important", "&:before": { display: "none" } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, px: 1.5, "& .MuiAccordionSummary-content": { my: 1 } }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("map.search.moreFilters")}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0 }}>
                                    {filterProfile !== "restaurant" && (
                                        <MultiFilter label={t("map.search.hotelGroups")} options={filterOptions.hotel_groups} value={draftFilters.hotel_group_ids} onChange={(value) => updateDraft("hotel_group_ids", value)} lang={lang} />
                                    )}
                                    <MultiFilter label={t("map.search.cuisines")} options={filterOptions.cuisines} value={draftFilters.cuisine_ids} onChange={(value) => updateDraft("cuisine_ids", value)} lang={lang} />
                                    <MultiFilter label={t("map.search.restaurantPrices")} options={filterOptions.restaurant_prices} value={draftFilters.restaurant_price_ids} onChange={(value) => updateDraft("restaurant_price_ids", value)} lang={lang} />
                                    <MultiFilter label={t("map.search.awards")} options={filterOptions.awards} value={draftFilters.award_ids} onChange={(value) => updateDraft("award_ids", value)} lang={lang} />

                                    {filterProfile !== "restaurant" && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: "#526072", mb: 1 }}>{t("map.search.priceTitle")}</Typography>
                                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                                                <TextField label={t("map.search.priceFrom")} value={draftFilters.price_min} onChange={(e) => updateDraft("price_min", e.target.value)} size="small" inputMode="decimal" />
                                                <TextField label={t("map.search.priceTo")} value={draftFilters.price_max} onChange={(e) => updateDraft("price_max", e.target.value)} size="small" inputMode="decimal" />
                                            </Box>
                                        </Box>
                                    )}

                                    <Divider />

                                    {filterProfile !== "restaurant" && (
                                        <MultiFilter label={t("map.search.roomDetails")} options={filterOptions.room_details} value={draftFilters.room_detail_ids} onChange={(value) => updateDraft("room_detail_ids", value)} lang={lang} />
                                    )}
                                    <MultiFilter label={t("map.search.amenities")} options={filterOptions.amenities} value={draftFilters.amenity_ids} onChange={(value) => updateDraft("amenity_ids", value)} lang={lang} />
                                    <MultiFilter label={t("map.search.leisure")} options={filterOptions.leisure_activities} value={draftFilters.leisure_activity_ids} onChange={(value) => updateDraft("leisure_activity_ids", value)} lang={lang} />
                                    <MultiFilter label={t("map.search.locations")} options={filterOptions.locations} value={draftFilters.location_ids} onChange={(value) => updateDraft("location_ids", value)} lang={lang} />
                                    <MultiFilter label={t("map.search.regions")} options={filterOptions.regions} value={draftFilters.region_ids} onChange={(value) => updateDraft("region_ids", value)} lang={lang} />
                                </AccordionDetails>
                            </Accordion>

                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button type="submit" variant="contained" fullWidth disableElevation endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important", transition: "transform 0.2s ease" }} />} sx={goldButtonSx}>
                                    {t("map.search.searchButton")}
                                </Button>
                                <Button type="button" variant="outlined" disableElevation endIcon={<RestartAltIcon sx={{ fontSize: "16px !important", transition: "transform 0.2s ease" }} />} onClick={() => { setDraftFilters(createEmptyFilters(defaultType)); setSearchParams({}); }} sx={outlineButtonSx}>
                                    {t("map.search.resetButton")}
                                </Button>
                            </Box>

                            {optionsLoading && <Typography variant="caption" color="text.secondary">{t("map.search.filtersLoading")}</Typography>}
                            {optionsError && <Alert severity="warning" sx={{ py: 0 }}>{optionsError}</Alert>}
                            <Typography variant="caption" color="text.secondary">
                                {String(t("map.search.resultsLabel")).replace("{{count}}", String(filteredMarkers.length))}
                            </Typography>
                        </Box>
                    </Paper>

                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden", height: { xs: advancedFiltersOpen ? "620px" : "520px", md: "100%" }, minHeight: { xs: advancedFiltersOpen ? "620px" : "520px", md: advancedFiltersOpen ? "980px" : "600px" }, position: "relative", transition: "min-height 0.25s ease" }}>
                        <HotelsMap markers={filteredMarkers} resizeKey={advancedFiltersOpen ? "advanced" : "basic"} />
                    </Paper>
                </Box>
            )}
        </>
    );
}
